import { stationDefinition } from "@frontier/content/stations";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { REGIONS } from "@frontier/content/world-layout";
import type { MissionView, Profile, ShipSnapshot, WorldSnapshot } from "@frontier/protocol/contracts";
import type { Point } from "./game/geometry";
import { galaxyRegionAt } from "./galaxy-navigation";
import type { GalaxyTarget } from "./galaxy-navigation";
import { GalaxyMapGraph } from "./GalaxyMapGraph";
import { GalaxyMapDetails, galaxyMapContracts } from "./GalaxyMapDetails";
import { GalaxyMapPopover } from "./GalaxyMapPopover";
import { STATION_CHART_POSITIONS, GALAXY_CHART_SIZE, GALAXY_REGION_POSITIONS } from "./galaxy-layout";
import { fitGalaxyView } from "./galaxy-viewport";
import { useGalaxyViewport } from "./use-galaxy-viewport";
import "./galaxy-map.css";

export interface GalaxyMapProps {
  world: WorldSnapshot;
  pilot: ShipSnapshot;
  profile: Profile;
  missions: readonly MissionView[];
  connected: boolean;
  onClose: () => void;
  onNavigate: (point: Point) => void;
  onJoinEvent: (id: string) => void;
}

export function GalaxyMap(props: GalaxyMapProps) {
  const [selected, setSelected] = useState<GalaxyTarget | null>(null);
  const [pinned, setPinned] = useState(false);
  const [eventId, setEventId] = useState("");
  const root = useRef<HTMLElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const selectedRef = useRef(selected);
  const pinnedRef = useRef(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearLeave = useCallback(() => {
    if (leaveTimer.current !== null) clearTimeout(leaveTimer.current);
    leaveTimer.current = null;
  }, []);
  const dismiss = useCallback(() => {
    clearLeave();
    pinnedRef.current = false;
    selectedRef.current = null;
    setSelected(null);
    setPinned(false);
  }, [clearLeave]);
  const camera = useGalaxyViewport(dismiss);
  const onClose = useRef(props.onClose);
  onClose.current = props.onClose;
  const headingId = useId();
  const popupId = useId();
  const currentRegion = galaxyRegionAt(props.world, props.pilot);
  const pointFor = (target: GalaxyTarget) => {
    const id = target.kind === "station"
      ? stationDefinition(target.id ?? "meridian").sectorId
      : target.id;
    const definition = REGIONS.find((region) => region.id === id);
    if (!definition) return null;
    const position = target.kind === "station" ? STATION_CHART_POSITIONS[target.id ?? "meridian"] : GALAXY_REGION_POSITIONS[definition.id];
    return {
      x: position.x * GALAXY_CHART_SIZE.width / 100,
      y: position.y * GALAXY_CHART_SIZE.height / 100,
    };
  };
  const preview = (target: GalaxyTarget) => {
    clearLeave();
    if (pinnedRef.current) return;
    selectedRef.current = target;
    setSelected(target);
  };
  const leave = () => {
    clearLeave();
    if (!pinnedRef.current) leaveTimer.current = setTimeout(dismiss, 180);
  };
  const select = (target: GalaxyTarget) => {
    clearLeave();
    pinnedRef.current = true;
    selectedRef.current = target;
    setSelected(target);
    setPinned(true);
    setEventId("");
  };
  useEffect(() => {
    const previousFocus = document.activeElement;
    close.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (selectedRef.current) dismiss();
        else onClose.current();
      }
      if (event.target instanceof HTMLSelectElement) return;
      if (["+", "=", "-", "0"].includes(event.key)) {
        event.preventDefault();
        dismiss();
        if (event.key === "0") camera.reset();
        else camera.zoom(event.key === "-" ? .8 : 1.25);
      }
      if (event.key !== "Tab") return;
      const buttons = root.current?.querySelectorAll<HTMLElement>("[data-galaxy-focus]:not(:disabled)");
      const first = buttons?.[0];
      const last = buttons?.[buttons.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && (document.activeElement === first || !root.current?.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !root.current?.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      clearLeave();
      document.removeEventListener("keydown", keydown);
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [camera.reset, camera.zoom, clearLeave, dismiss]);
  const selectedPoint = selected ? pointFor(selected) : null;
  const minimum = fitGalaxyView(camera.size).scale;
  return (
    <section className="galaxy-map" ref={root} role="dialog" aria-modal="true" aria-labelledby={headingId}>
      <header className="galaxy-map-header">
        <div>
          <span className="galaxy-eyebrow">{props.world.regions.filter((region) => region.id !== "training").length} ОБЛАСТЕЙ · ТРИ ФРАКЦИИ</span>
          <h1 id={headingId}>КАРТА ГАЛАКТИКИ</h1>
        </div>
        <div className="galaxy-map-controls" aria-label="Масштаб карты">
          <button type="button" data-galaxy-focus aria-label="Уменьшить карту" disabled={camera.view.scale <= minimum} onClick={() => { dismiss(); camera.zoom(.8); }}>−</button>
          <button type="button" data-galaxy-focus aria-label="Вся карта" title="Показать всю карту" onClick={() => { dismiss(); camera.reset(); }}>⌖</button>
          <button type="button" data-galaxy-focus aria-label="Увеличить карту" disabled={camera.view.scale >= minimum * 4} onClick={() => { dismiss(); camera.zoom(1.25); }}>+</button>
        </div>
        <button type="button" ref={close} className="galaxy-map-close" aria-label="Закрыть карту галактики" data-galaxy-focus onClick={props.onClose}>×</button>
      </header>
      <div className="galaxy-map-viewport" ref={camera.viewport} {...camera.handlers} onPointerLeave={leave}
        onClick={(event) => {
          if (event.target instanceof Element && !event.target.closest("[data-region-id], [data-station-region], [data-galaxy-ui]")) dismiss();
        }}>
        <GalaxyMapGraph world={props.world} contracts={galaxyMapContracts(props.profile, props.missions)} currentRegionId={currentRegion?.id ?? null}
          docked={props.pilot.docked} selected={selected} onSelect={select} onPreview={preview} onLeave={leave}
          onFocus={(target) => { const point = pointFor(target); if (point) camera.reveal(point); preview(target); }}
          popupId={popupId} pinned={pinned} view={camera.view} />
        {selected && selectedPoint && (
          <GalaxyMapPopover id={popupId} pinned={pinned} size={camera.size}
            clearance={Math.max(24, camera.view.scale * 75)}
            anchor={{ x: selectedPoint.x * camera.view.scale + camera.view.x, y: selectedPoint.y * camera.view.scale + camera.view.y }}
            onHold={clearLeave} onLeave={leave} onDismiss={dismiss}>
            <GalaxyMapDetails {...props} selected={selected} interactive={pinned} eventId={eventId} onEventChange={setEventId} />
          </GalaxyMapPopover>
        )}
        <div className="galaxy-map-gesture-hint" aria-hidden="true">КОЛЕСО — МАСШТАБ · ПЕРЕТАСКИВАНИЕ — ПЕРЕМЕЩЕНИЕ</div>
      </div>
    </section>
  );
}
